/**
 * KAN-976: диалог пополнения кошелька: сумма, быстрые суммы, проверка границ
 * из ответа кошелька. После успешного POST браузер уходит на оплату (хук).
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useStartTopup } from '@/hooks/useWallet';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import {
  QUICK_TOPUP_AMOUNTS,
  formatRub,
  parseAmountInput,
  toAmountString,
  validateTopupAmount,
} from '@/lib/wallet';

interface TopupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  minAmount: number;
  maxAmount: number;
}

export function TopupDialog({ open, onOpenChange, minAmount, maxAmount }: TopupDialogProps) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const topup = useStartTopup();

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setValue('');
      setError(null);
      topup.reset();
    }
    onOpenChange(next);
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const validation = validateTopupAmount(value, minAmount, maxAmount);
    setError(validation);
    const amount = parseAmountInput(value);
    if (validation || amount === null) return;
    topup.mutate(toAmountString(amount));
  };

  const serverError = topup.isError
    ? extractErrorMessage(topup.error, 'Не удалось создать платёж, попробуйте ещё раз')
    : null;
  const shownError = error ?? serverError;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="wallet-topup-dialog">
        <form onSubmit={submit} noValidate>
          <DialogHeader>
            <DialogTitle>Пополнение баланса</DialogTitle>
            <DialogDescription>
              От {formatRub(minAmount)} до {formatRub(maxAmount)}. Оплата через ЮKassa.
            </DialogDescription>
          </DialogHeader>

          <div className="my-4 space-y-3">
            <Label htmlFor="wallet-topup-amount">Сумма, руб.</Label>
            <Input
              id="wallet-topup-amount"
              data-testid="wallet-topup-amount"
              inputMode="decimal"
              autoComplete="off"
              value={value}
              aria-invalid={shownError ? true : undefined}
              aria-describedby={shownError ? 'wallet-topup-error' : undefined}
              onChange={(e) => {
                setValue(e.target.value);
                setError(null);
              }}
            />
            <div className="flex flex-wrap gap-2">
              {QUICK_TOPUP_AMOUNTS.map((amount) => (
                <Button
                  key={amount}
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid={`wallet-topup-quick-${amount}`}
                  onClick={() => {
                    setValue(String(amount));
                    setError(null);
                  }}
                >
                  {formatRub(amount)}
                </Button>
              ))}
            </div>
            {shownError && (
              <p
                id="wallet-topup-error"
                role="alert"
                data-testid="wallet-topup-error"
                className="text-sm text-red-600"
              >
                {shownError}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Отмена
            </Button>
            <Button type="submit" data-testid="wallet-topup-submit" disabled={topup.isPending || topup.isSuccess}>
              {topup.isPending || topup.isSuccess ? 'Переходим к оплате...' : 'Перейти к оплате'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
